import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from './AuthLayout';
import Input from '../ui/Input';
import Button from '../ui/Button';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  const { signIn, error: authError, statusMessage, clearError } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Clear auth error when user types
    if (authError) {
      clearError();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      await signIn(formData.email, formData.password);
      console.log('Login successful, redirecting to dashboard');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout 
      title="Welcome Back" 
      subtitle="Sign in to your StateSync account"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <Input
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="Enter your email address"
          error={errors.email}
          required
          autoComplete="email"
          className="text-sm"
        />

        {/* Password */}
        <Input
          label="Password"
          type="password"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          placeholder="Enter your password"
          error={errors.password}
          required
          autoComplete="current-password"
          className="text-sm"
        />

        {/* Auth Error with enhanced status handling */}
        {authError && (
          <div className="bg-error-subtle border border-border-error rounded-lg p-3">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-error mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-error">
                <p className="font-medium">{getErrorTitle(authError)}</p>
                <p className="text-xs mt-1">{authError}</p>
                {authError.includes('pending') && (
                  <div className="mt-2 text-xs">
                    <p>• Check your email for updates</p>
                    <p>• Contact administrator if needed</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Status Message (for account status issues) */}
        {statusMessage && (
          <div className="bg-warning-subtle border border-border-primary rounded-lg p-3">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-warning mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-warning">
                <p className="font-medium">Account Status Notice</p>
                <p className="text-xs mt-1">{statusMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Account Status Help */}
        {(authError?.includes('pending') || authError?.includes('approval')) && (
          <div className="bg-info-subtle border border-border-primary rounded-lg p-3">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-info mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-info">
                <p className="font-medium mb-1">Account Pending Approval</p>
                <div className="text-xs space-y-1">
                  <p>• Your registration was successful</p>
                  <p>• Admin approval is required before you can sign in</p>
                  <p>• You'll receive an email once approved</p>
                  <p>• This process typically takes 1-2 business days</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Forgot Password Link */}
        <div className="text-right">
          <Link 
            to="/forgot-password"
            className="text-sm text-text-link hover:text-text-link-hover transition-colors"
          >
            Forgot your password?
          </Link>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          loading={loading}
          disabled={loading}
          className="w-full"
          size="md"
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>

        {/* Sign Up Link */}
        <div className="text-center pt-4 border-t border-border-primary">
          <p className="text-sm text-text-secondary">
            Don't have an account?{' '}
            <Link 
              to="/signup"
              className="text-text-link hover:text-text-link-hover font-medium transition-colors"
            >
              Sign up here
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

// Helper function to get error title based on error message
const getErrorTitle = (errorMessage) => {
  if (errorMessage.includes('pending')) return 'Account Pending Approval';
  if (errorMessage.includes('suspended')) return 'Account Suspended';
  if (errorMessage.includes('inactive')) return 'Account Inactive';
  if (errorMessage.includes('rejected')) return 'Account Rejected';
  if (errorMessage.includes('password')) return 'Invalid Credentials';
  if (errorMessage.includes('not found')) return 'Account Not Found';
  return 'Sign In Failed';
};

export default Login;